/**
 * `<ForecastPillSection/>` — Forecast pill in the persistent right panel (#162).
 *
 * Mirrors the wireframe at `docs/research/ui-wireframes-page-by-page.md`
 * §4 lines 1413-1417 (lead variant) + 1465-1467 (member variant). A
 * thin one-line summary of the forecast with a deep link to the
 * appropriate detail page.
 *
 * **Variants.** `audience="lead"` reads the team's hire-by projection
 * and surfaces `Sustainable through August.`; `audience="member"`
 * reads the worker's sustainability and surfaces `Sustainable next N
 * cycles`. The two variants share the pill chrome but query
 * different endpoints.
 *
 * **Self-hide states.** Returns `null` on fetch error, on `no_data`,
 * or when the resolved team has the `forecast` component off. The
 * panel pill is meant to be ambient context — an empty pill is
 * noisier than no pill.
 *
 * **Focal-component discipline** per `feedback_ui_section_focal_component.md`:
 * the focal element is the headline summary line — the verdict in one
 * glance. The "See detail →" link is calm chrome.
 *
 * **Component gate.** Lead variant is wrapped by the panel parent in
 * `<ComponentGate component="forecast" ifOff="hide">` once the panel
 * mounts. Member variant gates inside the section because the team_id
 * isn't known at mount time (it resolves from the sustainability
 * fetch's `scope.team_id`).
 */
import { useQuery } from "@tanstack/react-query";
import { LineChart } from "lucide-react";

import { apiFetch } from "../api/client";
import { useTeamFeature } from "../api/useTeamFeature";
import { useAuth } from "../lib/AuthContext";
import { forecastHireProjectionKey, meForecastSustainabilityKey } from "../lib/forecastKeys";
import { AmbientErrorNotice, isUnexpectedApiError } from "./AmbientErrorNotice";
import { AmbientPill } from "./AmbientPill";

type Verdict = "sustainable" | "tightening" | "needs_attention";

interface LeadResponseShape {
  team_id: string;
  sustainable_through_month: string | null;
  hire_by_month: string | null;
}

interface MemberResponseShape {
  state: "ready" | "no_data";
  scope: { team_id: string } | null;
  sustainable_for_cycles: number | null;
  verdict: Verdict | null;
}

function formatMonthShort(yyyymm: string | null): string {
  if (!yyyymm) return "—";
  const [y, m] = yyyymm.split("-").map(Number);
  if (!y || !m) return yyyymm;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" });
}

interface LeadPillProps {
  teamId: string;
}

export function ForecastPillSectionLead({ teamId }: LeadPillProps) {
  const { accountId } = useAuth();
  // Only fetch when the `forecast` component is ON for this team. Firing
  // unconditionally produced a 404 (the backend component gate) for
  // gated-off teams, which logs as browser console noise. `useTeamFeature`
  // is a shared, cached query (deduped across the gated pill sections).
  const forecastOn = useTeamFeature(teamId, "forecast");
  const projectionQuery = useQuery({
    queryKey: forecastHireProjectionKey(teamId, accountId ?? ""),
    enabled: Boolean(teamId && accountId) && forecastOn === true,
    queryFn: () => apiFetch<LeadResponseShape>(`/api/teams/${teamId}/forecast/hire_projection`),
  });

  // An unexpected error (broken session / server fault) surfaces a calm
  // notice rather than vanishing; an expected 404 (gated-off team) keeps
  // hiding. The query is feature-gated above, so a 404 should be rare.
  if (projectionQuery.isError) {
    if (isUnexpectedApiError(projectionQuery.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void projectionQuery.refetch()}
          label="Forecast"
          testId="forecast-pill-section-lead-error"
        />
      );
    }
    return null;
  }
  if (projectionQuery.data?.sustainable_through_month == null) return null;

  return (
    <AmbientPill
      icon={LineChart}
      title="Forecast"
      detailTo={`/teams/${teamId}/forecast`}
      detailTestId="forecast-pill-detail-link"
      testId="forecast-pill-section-lead"
      isLoading={projectionQuery.isLoading}
      skeletonClassName="h-5"
    >
      <p className="text-xs text-fg-muted" data-testid="forecast-pill-summary">
        Sustainable through{" "}
        <strong className="text-fg">
          {formatMonthShort(projectionQuery.data?.sustainable_through_month ?? null)}
        </strong>
        .
      </p>
    </AmbientPill>
  );
}

// ─── Member variant ──────────────────────────────────────────────────

export function ForecastPillSectionMember() {
  const { accountId } = useAuth();
  const sustainabilityQuery = useQuery({
    queryKey: meForecastSustainabilityKey(accountId),
    enabled: Boolean(accountId),
    queryFn: () => apiFetch<MemberResponseShape>("/api/account/me/forecast/sustainability"),
  });

  // The member-side pill self-gates on the resolved team_id. The
  // `useTeamFeature` hook tolerates `undefined` (returns undefined
  // while loading) — once the fetch resolves we know whether to render.
  const forecastEnabled = useTeamFeature(sustainabilityQuery.data?.scope?.team_id, "forecast");

  if (sustainabilityQuery.isError) {
    if (isUnexpectedApiError(sustainabilityQuery.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void sustainabilityQuery.refetch()}
          label="Your forecast"
          testId="forecast-pill-section-member-error"
        />
      );
    }
    return null;
  }
  if (sustainabilityQuery.data?.state === "no_data") return null;
  if (sustainabilityQuery.data?.scope?.team_id && forecastEnabled === false) return null;

  const cycles = sustainabilityQuery.data?.sustainable_for_cycles ?? null;

  return (
    <AmbientPill
      icon={LineChart}
      title="Forecast"
      ariaLabel="Your forecast"
      detailTo="/me/forecast"
      detailTestId="forecast-pill-detail-link"
      testId="forecast-pill-section-member"
      isLoading={sustainabilityQuery.isLoading || cycles == null}
      skeletonClassName="h-5"
    >
      <p className="text-xs text-fg-muted" data-testid="forecast-pill-summary">
        Sustainable next <strong className="text-fg">{cycles}</strong>{" "}
        {cycles === 1 ? "schedule" : "schedules"}.
      </p>
    </AmbientPill>
  );
}
